/**
 * Document Field Tracking Utility
 * 
 * This utility provides functions to track document fields in workflows
 * to prevent duplication and ensure consistent handling of document references.
 */

// Store tracked document fields
let trackedDocumentFields = {};

/**
 * Track a document field by its name and URL
 * 
 * @param {Object} field - The document field to track
 * @returns {boolean} - True if the field was newly tracked, false if it was already tracked
 */
export const trackDocumentField = (field) => {
  if (!field) return false;
  
  const fieldKey = field.name || field.code || field.title;
  const documentUrl = field.value || field.defaultValue || '';
  
  if (!fieldKey) return false;
  
  // Create a unique key for the field based on name and URL
  const trackingKey = `${fieldKey}:${documentUrl}`;
  
  // Check if the field is already tracked
  if (trackedDocumentFields[trackingKey]) {
    console.log(`Document field already tracked: ${fieldKey}`);
    return false;
  }
  
  // Track the field
  trackedDocumentFields[trackingKey] = {
    field,
    timestamp: new Date().getTime()
  };
  
  console.log(`Tracked document field: ${fieldKey}`);
  return true;
};

/**
 * Check if a document field is already tracked
 * 
 * @param {Object} field - The document field to check
 * @returns {boolean} - True if the field is already tracked
 */
export const isDocumentFieldTracked = (field) => {
  if (!field) return false;
  
  const fieldKey = field.name || field.code || field.title;
  const documentUrl = field.value || field.defaultValue || '';
  
  if (!fieldKey) return false;
  
  // Create a unique key for the field based on name and URL
  const trackingKey = `${fieldKey}:${documentUrl}`;
  
  return !!trackedDocumentFields[trackingKey];
};

/**
 * Get all tracked document fields
 * 
 * @returns {Object} - Object containing all tracked document fields
 */
export const getTrackedDocumentFields = () => {
  return { ...trackedDocumentFields };
};

/**
 * Reset document field tracking
 * This should be called when workflow context changes
 */
export const resetDocumentFieldTracking = () => {
  trackedDocumentFields = {};
  console.log('Document field tracking reset');
};

/**
 * Get the first tracked instance of a document field by name
 * 
 * @param {string} fieldName - The name of the document field
 * @returns {Object|null} - The tracked document field or null if not found
 */
export const getTrackedDocumentFieldByName = (fieldName) => {
  if (!fieldName) return null;
  
  // Find the first tracked field with the given name
  const trackingKeys = Object.keys(trackedDocumentFields);
  for (const key of trackingKeys) {
    if (key.startsWith(`${fieldName}:`)) {
      return trackedDocumentFields[key].field;
    }
  }
  
  return null;
};

/**
 * Remove a tracked document field
 * 
 * @param {Object} field - The document field to remove
 * @returns {boolean} - True if the field was removed, false if it wasn't tracked
 */
export const removeTrackedDocumentField = (field) => {
  if (!field) return false;
  
  const fieldKey = field.name || field.code || field.title;
  const documentUrl = field.value || field.defaultValue || '';
  
  if (!fieldKey) return false;
  
  // Create a unique key for the field based on name and URL
  const trackingKey = `${fieldKey}:${documentUrl}`;
  
  // Check if the field is tracked
  if (!trackedDocumentFields[trackingKey]) {
    return false;
  }
  
  // Remove the field
  delete trackedDocumentFields[trackingKey];
  console.log(`Removed tracked document field: ${fieldKey}`);
  return true;
};

export default {
  trackDocumentField,
  isDocumentFieldTracked,
  getTrackedDocumentFields,
  resetDocumentFieldTracking,
  getTrackedDocumentFieldByName,
  removeTrackedDocumentField
};
